/**
 * MCPSettings - manage MCP tokens for Claude Desktop integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { ChevronDown, Check, Copy, X, Info, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import { mcpApi, type MCPToken, type MCPTokenCreated } from '@services/api';
import { saveBlob } from '@/utils/download';
import { useAuthStore } from '@stores/authStore';
import { organizationsApi, type Organization } from '@services/api';

const CLAUDE_CODE_INSTALL_COMMANDS = `/plugin marketplace add ezeeFlop/claude-plugins
/plugin install neokanban@sponge-theory`;

export default function MCPSettings() {
  const { t } = useTranslation();
  const { currentOrganization } = useAuthStore();

  // Tokens state
  const [tokens, setTokens] = useState<MCPToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Organizations for selector
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Extension bundle download state
  const [isDownloadingExtension, setIsDownloadingExtension] = useState(false);

  // Create token state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<number>(365);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Newly created token (shown once)
  const [createdToken, setCreatedToken] = useState<MCPTokenCreated | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [copiedCommands, setCopiedCommands] = useState(false);

  // Delete confirmation
  const [tokenToDelete, setTokenToDelete] = useState<MCPToken | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadTokens = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await mcpApi.list();
      setTokens(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MCP tokens');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    try {
      const orgs = await organizationsApi.list();
      setOrganizations(orgs);
      if (orgs.length > 0 && !selectedOrgId) {
        setSelectedOrgId(currentOrganization?.id || orgs[0]!.id);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  }, [currentOrganization, selectedOrgId]);

  useEffect(() => {
    loadTokens();
    loadOrganizations();
  }, [loadTokens, loadOrganizations]);

  /**
   * Fetch the .mcpb bundle through the API client and save it.
   *
   * The endpoint is authenticated: a plain download link sends no bearer token
   * and the browser reports "file not available".
   */
  const handleDownloadExtension = async () => {
    setIsDownloadingExtension(true);
    try {
      const bundle = await mcpApi.downloadExtension();
      saveBlob(bundle, 'neokanban.mcpb');
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('settings.mcpDownloadExtensionFailed', 'Failed to download the extension bundle')
      );
    } finally {
      setIsDownloadingExtension(false);
    }
  };

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTokenName.trim() || !selectedOrgId) return;

    setIsCreating(true);
    setCreateError(null);

    try {
      const token = await mcpApi.create({
        name: newTokenName.trim(),
        organizationId: selectedOrgId,
        expiresInDays,
      });
      setCreatedToken(token);
      setNewTokenName('');
      setExpiresInDays(365);
      setShowCreateModal(false);
      await loadTokens();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create MCP token');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyToken = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = createdToken.token;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  const handleCopyConfig = async () => {
    if (!createdToken) return;
    const config = getConfigSnippet(createdToken);
    try {
      await navigator.clipboard.writeText(config);
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = config;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    }
  };

  const handleCopyCommands = async () => {
    try {
      await navigator.clipboard.writeText(CLAUDE_CODE_INSTALL_COMMANDS);
      setCopiedCommands(true);
      setTimeout(() => setCopiedCommands(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = CLAUDE_CODE_INSTALL_COMMANDS;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedCommands(true);
      setTimeout(() => setCopiedCommands(false), 2000);
    }
  };

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return;

    setIsDeleting(true);
    try {
      await mcpApi.revoke(tokenToDelete.id);
      setTokens(tokens.filter((t) => t.id !== tokenToDelete.id));
      setTokenToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke MCP token');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getConfigSnippet = (token: MCPTokenCreated) => {
    const apiUrl = window.location.origin;
    const mcpEndpoint = `${apiUrl}/api/v1/mcp/messages`;

    return `{
  "mcpServers": {
    "neokanban": {
      "command": "npx",
      "args": [
        "-y",
        "@modelcontextprotocol/server-http"
      ],
      "env": {
        "HTTP_URL": "${mcpEndpoint}",
        "HTTP_HEADERS": "{\\"Authorization\\": \\"Bearer ${token.token}\\"}"
      }
    }
  }
}`;
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
            {t('settings.mcpSettings', 'MCP Integration')}
          </h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {t(
              'settings.mcpDescription',
              'Connect NeoKanban to Claude Desktop and Claude Code using the Model Context Protocol (MCP)'
            )}
          </p>
        </div>
        {!showCreateModal && !createdToken && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
          >
            {t('settings.generateToken', 'Generate Token')}
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">
              {t('settings.mcpAbout', 'What is MCP?')}
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              {t(
                'settings.mcpAboutText',
                'The Model Context Protocol enables Claude Desktop to interact with NeoKanban directly. Create projects, tasks, and search your workspace from Claude. Uses the official MCP HTTP bridge - works on all platforms.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Download Extension Button */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-primary-900 dark:text-primary-300">
              {t('settings.mcpDownloadExtension', 'Claude Desktop Extension')}
            </h3>
            <p className="text-sm text-primary-700 dark:text-primary-400 mt-1">
              {t(
                'settings.mcpDownloadExtensionDesc',
                'Download the .mcpb extension bundle and drag it into Claude Desktop Settings → Extensions for one-click installation.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadExtension}
            disabled={isDownloadingExtension}
            className="ml-4 flex items-center gap-2 px-6 py-3 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            {isDownloadingExtension
              ? t('common.loading', 'Loading...')
              : t('settings.downloadExtension', 'Download .mcpb')}
          </button>
        </div>
      </div>

      {/* Claude Code Plugin */}
      <div className="bg-gradient-to-r from-violet-50 to-violet-100 dark:from-violet-900/20 dark:to-violet-800/20 border border-violet-200 dark:border-violet-800 rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <Terminal className="w-5 h-5 text-violet-600 dark:text-violet-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-violet-900 dark:text-violet-300">
              {t('settings.mcpClaudeCode', 'Claude Code Plugin')}
            </h3>
            <p className="text-sm text-violet-700 dark:text-violet-400 mt-1">
              {t(
                'settings.mcpClaudeCodeDesc',
                'Install the NeoKanban plugin from the Sponge Theory marketplace. During installation, Claude Code opens an interactive form asking for your API URL and MCP token — generate the token on this page first.'
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <code className="p-3 bg-neutral-900 dark:bg-neutral-800 text-violet-300 text-sm font-mono rounded overflow-x-auto whitespace-pre">
            {CLAUDE_CODE_INSTALL_COMMANDS}
          </code>
          <button
            onClick={handleCopyCommands}
            className="self-end px-3 py-1.5 text-sm bg-violet-600 text-white rounded-md hover:bg-violet-700 flex items-center gap-2"
          >
            {copiedCommands ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copiedCommands ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
          </button>
        </div>
        <p className="text-xs text-violet-700 dark:text-violet-400">
          {t(
            'settings.mcpClaudeCodeConfigureHint',
            'Missed the form? Run /plugin configure neokanban@sponge-theory to enter the token later.'
          )}
        </p>
      </div>

      {/* Newly created token display */}
      {createdToken && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5">
              <Check className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                {t('settings.mcpTokenCreated', 'MCP Token created successfully!')}
              </p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                {t(
                  'settings.mcpTokenWarning',
                  "Copy this token now and add it to your Claude Desktop config. You won't be able to see it again!"
                )}
              </p>
            </div>
          </div>

          {/* Token */}
          <div>
            <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-2">
              {t('settings.mcpToken', 'MCP Token')}
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-neutral-900 dark:bg-neutral-800 text-green-400 text-sm font-mono rounded overflow-x-auto">
                {createdToken.token}
              </code>
              <button
                onClick={handleCopyToken}
                className="px-3 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
                title={t('common.copy', 'Copy')}
              >
                {copiedToken ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Config snippet */}
          <div>
            <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-2">
              {t('settings.mcpConfigSnippet', 'Claude Desktop Configuration')}
            </label>
            <div className="flex flex-col gap-2">
              <code className="p-3 bg-neutral-900 dark:bg-neutral-800 text-green-400 text-xs font-mono rounded overflow-x-auto whitespace-pre">
                {getConfigSnippet(createdToken)}
              </code>
              <button
                onClick={handleCopyConfig}
                className="self-end px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
              >
                {copiedConfig ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedConfig ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
              </button>
            </div>
          </div>

          {/* Claude Code hint */}
          <p className="text-xs text-green-700 dark:text-green-400">
            {t(
              'settings.mcpTokenClaudeCodeHint',
              'Using Claude Code? Install the plugin (see "Claude Code Plugin" above) and paste this token when the install form asks for it.'
            )}
          </p>

          <button
            onClick={() => setCreatedToken(null)}
            className="text-sm text-green-700 dark:text-green-400 hover:underline"
          >
            {t('settings.mcpTokenDismiss', "I've saved the token and config")}
          </button>
        </div>
      )}

      {/* Active tokens list */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.mcpActiveTokens', 'Active MCP Tokens')}
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tokens.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-neutral-300 dark:text-neutral-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
            <p>{t('settings.mcpNoTokens', 'No MCP tokens yet')}</p>
            <p className="text-sm mt-1">
              {t('settings.mcpNoTokensDescription', 'Generate a token to connect Claude Desktop')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tokens.map((token) => (
              <div
                key={token.id}
                className={clsx(
                  'flex items-center justify-between p-4 border rounded-lg',
                  token.isExpired
                    ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                    : 'border-neutral-200 dark:border-neutral-700'
                )}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div
                    className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                      token.isExpired
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-neutral-100 dark:bg-neutral-700'
                    )}
                  >
                    <svg
                      className={clsx(
                        'w-5 h-5',
                        token.isExpired
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-neutral-500 dark:text-neutral-400'
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                        {token.name}
                      </p>
                      {token.isExpired && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded flex-shrink-0">
                          {t('settings.expired', 'Expired')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
                      {token.tokenPrefix}...
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-neutral-500">
                      <span className="truncate">
                        {t('settings.organization', 'Organization')}: {token.organizationName}
                      </span>
                      <span>
                        {t('settings.created', 'Created')}: {formatDate(token.createdAt)}
                      </span>
                      {token.lastUsedAt && (
                        <span>
                          {t('settings.lastUsed', 'Last used')}: {formatDate(token.lastUsedAt)}
                        </span>
                      )}
                      {token.expiresAt && (
                        <span>
                          {t('settings.expires', 'Expires')}: {formatDate(token.expiresAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setTokenToDelete(token)}
                  className="ml-4 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex-shrink-0"
                >
                  {t('settings.revoke', 'Revoke')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Setup Instructions */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.mcpSetupInstructions', 'Claude Desktop Setup (manual config)')}
        </h3>
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 space-y-4">
          <ol className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center text-xs font-medium">
                1
              </span>
              <span>
                {t(
                  'settings.mcpStep1',
                  'Generate an MCP token using the button above and copy the configuration snippet'
                )}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center text-xs font-medium">
                2
              </span>
              <span>
                {t(
                  'settings.mcpStep2',
                  'Open your Claude Desktop config file (Settings → Developer → Edit Config)'
                )}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center text-xs font-medium">
                3
              </span>
              <span>
                {t(
                  'settings.mcpStep3',
                  'Paste the configuration snippet into your config file under the "mcpServers" section'
                )}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center text-xs font-medium">
                4
              </span>
              <span>
                {t('settings.mcpStep4', 'Restart Claude Desktop to activate the NeoKanban integration')}
              </span>
            </li>
          </ol>
        </div>
      </div>

      {/* Create Token Modal */}
      <Dialog.Root open={showCreateModal} onOpenChange={setShowCreateModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content
            className={clsx(
              'fixed left-1/2 top-1/2 w-full max-w-md max-h-[85vh] overflow-y-auto',
              '-translate-x-1/2 -translate-y-1/2 z-50',
              'rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900'
            )}
          >
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('settings.mcpGenerateToken', 'Generate MCP Token')}
            </Dialog.Title>

            {createError && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateToken} className="mt-4 space-y-4">
              {/* Token Name */}
              <div>
                <label
                  htmlFor="tokenName"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('settings.tokenName', 'Token Name')} *
                </label>
                <input
                  type="text"
                  id="tokenName"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder={t('settings.mcpTokenNamePlaceholder', 'e.g., My Laptop')}
                  className={clsx(
                    'mt-1 block w-full rounded-md border px-3 py-2',
                    'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                    'border-neutral-300 dark:border-neutral-700',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                  required
                />
              </div>

              {/* Organization Selector */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {t('settings.organization', 'Organization')} *
                </label>
                <Select.Root value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <Select.Trigger
                    className={clsx(
                      'flex items-center justify-between w-full px-3 py-2 text-sm rounded-md border',
                      'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white',
                      'border-neutral-300 dark:border-neutral-700',
                      'hover:bg-neutral-50 dark:hover:bg-neutral-700',
                      'focus:outline-none focus:ring-2 focus:ring-primary-500'
                    )}
                  >
                    <Select.Value />
                    <Select.Icon>
                      <ChevronDown className="w-4 h-4" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content
                      className={clsx(
                        'overflow-hidden bg-white dark:bg-neutral-800 rounded-md shadow-lg',
                        'border border-neutral-200 dark:border-neutral-700'
                      )}
                    >
                      <Select.Viewport className="p-1">
                        {organizations.map((org) => (
                          <Select.Item
                            key={org.id}
                            value={org.id}
                            className={clsx(
                              'relative flex items-center px-8 py-2 text-sm rounded cursor-pointer',
                              'text-neutral-900 dark:text-white',
                              'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                              'focus:bg-neutral-100 dark:focus:bg-neutral-700 outline-none'
                            )}
                          >
                            <Select.ItemText>{org.name}</Select.ItemText>
                            <Select.ItemIndicator className="absolute left-2">
                              <Check className="w-4 h-4" />
                            </Select.ItemIndicator>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>

              {/* Expiration */}
              <div>
                <label
                  htmlFor="expiresInDays"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('settings.mcpTokenExpiry', 'Expires in (days)')}
                </label>
                <input
                  type="number"
                  id="expiresInDays"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10))}
                  className={clsx(
                    'mt-1 block w-full rounded-md border px-3 py-2',
                    'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                    'border-neutral-300 dark:border-neutral-700',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                  required
                />
                <p className="mt-1 text-xs text-neutral-500">
                  {t('settings.mcpTokenExpiryHelp', 'Recommended: 365 days (1 year)')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTokenName('');
                    setExpiresInDays(365);
                    setCreateError(null);
                  }}
                  className={clsx(
                    'rounded-lg px-4 py-2 text-sm font-medium',
                    'text-neutral-700 hover:bg-neutral-100',
                    'dark:text-neutral-300 dark:hover:bg-neutral-800'
                  )}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newTokenName.trim() || !selectedOrgId}
                  className={clsx(
                    'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
                    'bg-primary-600 text-white hover:bg-primary-700',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  {isCreating ? t('common.creating', 'Creating...') : t('settings.generateToken', 'Generate Token')}
                </button>
              </div>
            </form>

            <Dialog.Close asChild>
              <button
                className={clsx(
                  'absolute right-4 top-4 rounded p-1',
                  'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-500',
                  'dark:hover:bg-neutral-800'
                )}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete confirmation modal */}
      <Dialog.Root open={!!tokenToDelete} onOpenChange={(open) => !open && setTokenToDelete(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content
            className={clsx(
              'fixed left-1/2 top-1/2 w-full max-w-md',
              '-translate-x-1/2 -translate-y-1/2 z-50',
              'rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900'
            )}
          >
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('settings.mcpRevokeToken', 'Revoke MCP Token')}
            </Dialog.Title>

            <p className="mt-2 text-neutral-600 dark:text-neutral-400">
              {t(
                'settings.mcpRevokeTokenWarning',
                'Are you sure you want to revoke this token? Claude Desktop will no longer be able to access NeoKanban using this token.'
              )}
            </p>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setTokenToDelete(null)}
                disabled={isDeleting}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDeleteToken}
                disabled={isDeleting}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-red-600 text-white hover:bg-red-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isDeleting ? t('common.revoking', 'Revoking...') : t('settings.revokeToken', 'Revoke Token')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
